import { DocumentPage } from "@loot-os/design-system/patterns/DocumentPage";
import type { ReactNode } from "react";
import { useEffect } from "react";

/**
 * The frame the Terms of Service and the Privacy Policy share: the design system's DocumentPage
 * (the title, when the text last changed, and the theme's prose type, applied from the wrapper
 * so the documents stay plain semantic HTML). A `#fragment` in the URL scrolls to that section
 * once the page has mounted, the way a link into the middle of a policy expects.
 */
export function PolicyDocument({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}) {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <DocumentPage
      title={title}
      meta={
        <>
          <em>Last updated</em>: {lastUpdated}
        </>
      }
    >
      {children}
    </DocumentPage>
  );
}

/** A numbered section of a document; `id` makes it a link target. */
export function PolicySection({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number?: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id}>
      <h2>
        {number !== undefined ? `${number}. ` : ""}
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A lettered clause inside a section: "(a) Title. Body". */
export function PolicyClause({
  label,
  title,
  children,
}: {
  label: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <p>
      {label}
      {title ? (
        <>
          {" "}
          <u>{title}</u>.
        </>
      ) : null}{" "}
      {children}
    </p>
  );
}
